import { FuelAlertVariant } from "@deere/fuel-react";
import {
  Cancel,
  CheckCircle,
  DirectionsRun,
  LocalShipping,
  SvgIconComponent,
  Warning,
} from "@mui/icons-material";
import { GridSortModel } from "@mui/x-data-grid-pro";
import dayjs, { Dayjs } from "dayjs";
import { t } from "i18next";
import { Dispatch } from "react";
import { AllClaimsV2 } from "../../../api/claims/claimsInterfaces";
import { dayJSLang } from "../../../types";
import { HeaderFilterActionPayload } from "../../promotionsV2/PromotionDetails/type";
import {
  AllClaimsFilter,
  AllClaimsRow,
} from "../allClaimsV2/components/allClaimsGrid/AllClaimsGrid";
import {
  AllClaimsListActions,
  setDateFilterAction,
  setFilterAction,
  setPageNumberAction,
  setPageSizeAction,
  setSortParamsAction,
} from "../allClaimsV2/reducer/action";
import { AllClaimsState } from "../allClaimsV2/reducer/reducer";
import {
  getFilter,
  getSortModel,
  getStatusIconInfoList,
} from "../allClaimsV2/reducer/selector";
import {
  JDP_SHORTAGE_OVERAGE,
  VENDORE_DIRECT_SHORTAGE_OVERAGE,
} from "../constants";

export const commonDateFormat = "DD-MMM-YYYY";

export type IconType = {
  icon: SvgIconComponent;
  variant: FuelAlertVariant;
  type: "default" | "bold";
  name: string;
};

export async function handleAPICall<T>(
  asyncCall: () => Promise<T>,
  {
    loadingStatus,
    onSuccess,
    onFailure,
  }: {
    loadingStatus: (isLoading: boolean) => void;
    onSuccess: (data: T) => void;
    onFailure: (e: any) => void;
  }
) {
  loadingStatus(true);
  try {
    const result = await asyncCall();
    onSuccess(result);
  } catch (e) {
    console.error(e);
    onFailure(e);
  } finally {
    loadingStatus(false);
  }
}

export function createAllClaimsRows(
  allClaims: AllClaimsV2[],
  state: AllClaimsState,
  language: string
): AllClaimsRow[] {
  return allClaims.map((claim, index) => ({
    id: (index + 1).toString(),
    claimNumber: claim.claimNumber,
    dealerReference: claim.dealerReference,
    handlingUnitNumber: claim.huNumber,
    invoiceNumber: claim.invoiceNumber,
    notaFiscalNumber: claim.nfeNumber,
    createdBy: claim.createdBy,
    createdDate: formatDate(claim.createdDate, language),
    type: claim.claimType,
    warnings: claim.hasRejected ? "W" : "",
    statusIconInfo: getStatusIconInfoList(claim.claimsStatusList || [], state),
    dealerAccount: claim.accountId ?? "",
  }));
}

export type StatusTexts = {
  approvedByAllLevels: string;
  completed: string;
  csrApproved: string;
  furtherApprovalsNeeded: string;
  inProcess: string;
  inProgress: string;
  needAction: string;
  onHoldCustomerAction: string;
  open: string;
  pendingPayment: string;
  rejected: string;
  settled: string;
};

export const getStatusTexts = (
  translate: (key: string) => string
): StatusTexts => ({
  approvedByAllLevels: translate(
    "claims:claims.lineItem.status.approvedByAllLevels"
  ),
  completed: translate("claims:claims.lineItem.status.completed"),
  csrApproved: translate("claims:claims.lineItem.status.csrApproved"),
  furtherApprovalsNeeded: translate(
    "claims:claims.lineItem.status.furtherApprovalsNeeded"
  ),
  inProcess: translate("claims:claims.lineItem.status.inProcess"),
  inProgress: translate("claims:claims.lineItem.status.inProgress"),
  needAction: translate("claims:claims.lineItem.status.needAction"),
  onHoldCustomerAction: translate(
    "claims:claims.lineItem.status.onHoldCustomerAction"
  ),
  open: translate("claims:claims.lineItem.status.open"),
  pendingPayment: translate("claims:claims.lineItem.status.pendingPayment"),
  rejected: translate("claims:claims.lineItem.status.rejected"),
  settled: translate("claims:claims.lineItem.status.settled"),
});

export const getStatusIconInfo = (
  statusText: StatusTexts
): { [key: string]: IconType } => {
  const base: { [key: string]: IconType } = {
    approvedByAllLevels: {
      icon: CheckCircle,
      variant: "success",
      type: "bold",
      name: statusText.approvedByAllLevels,
    },
    completed: {
      icon: CheckCircle,
      variant: "success",
      type: "bold",
      name: statusText.completed,
    },
    csrApproved: {
      icon: CheckCircle,
      variant: "success",
      type: "bold",
      name: statusText.csrApproved,
    },
    furtherApprovalsNeeded: {
      icon: Warning,
      variant: "warning",
      type: "default",
      name: statusText.furtherApprovalsNeeded,
    },
    inProcess: {
      icon: DirectionsRun,
      variant: "info",
      type: "default",
      name: statusText.inProcess,
    },
    inProgress: {
      icon: DirectionsRun,
      variant: "info",
      type: "default",
      name: statusText.inProgress,
    },
    needAction: {
      icon: Warning,
      variant: "warning",
      type: "default",
      name: statusText.needAction,
    },
    onHoldCustomerAction: {
      icon: Warning,
      variant: "warning",
      type: "default",
      name: statusText.onHoldCustomerAction,
    },
    open: {
      icon: DirectionsRun,
      variant: "info",
      type: "default",
      name: statusText.open,
    },
    pendingPayment: {
      icon: LocalShipping,
      variant: "warning",
      type: "default",
      name: statusText.pendingPayment,
    },
    rejected: {
      icon: Cancel,
      variant: "error",
      type: "default",
      name: statusText.rejected,
    },
    settled: {
      icon: CheckCircle,
      variant: "success",
      type: "bold",
      name: statusText.settled,
    },
  };

  const withAliases: { [key: string]: IconType } = { ...base };
  Object.keys(base).forEach((key) => {
    withAliases[key.toLowerCase()] = base[key];
  });

  return withAliases;
};

export function formatDate(date: string, language: string) {
  return dayjs(date).locale(dayJSLang[language]).format(commonDateFormat);
}

export const dayJSFormatterForAPI = (value: Dayjs): string =>
  value.format("YYYY-MM-DD");

export function getPaginationProps(
  paginationDetails: {
    pageSize: number;
    currentPage: number;
    totalCount: number;
    nextPage: number;
    previousPage: number;
    lastPage: number;
    firstPage: number;
    showPagination: boolean;
  },
  dispatch: Dispatch<AllClaimsListActions>
) {
  return {
    ...paginationDetails,
    onRowsPerPageChange: (pageSize: number) => {
      dispatch(setPageSizeAction(pageSize));
    },
    onFirstPageClick: () => {
      dispatch(setPageNumberAction(paginationDetails.firstPage));
    },
    onPreviousClick: () => {
      dispatch(setPageNumberAction(paginationDetails.previousPage));
    },
    onLastPageClick: () => {
      dispatch(setPageNumberAction(paginationDetails.lastPage));
    },
    onNextClick: () => {
      dispatch(setPageNumberAction(paginationDetails.nextPage));
    },
  };
}

export function getFilterProps(
  state: AllClaimsState,
  dispatch: Dispatch<AllClaimsListActions>
) {
  return {
    filter: getFilter(state),
    applyFilter: (filter: HeaderFilterActionPayload) => {
      dispatch(setFilterAction(filter));
    },
    applyDateFilter: (filter: AllClaimsFilter) => {
      dispatch(setDateFilterAction(filter));
    },
  };
}

export function getSortProps(
  state: AllClaimsState,
  dispatch: Dispatch<AllClaimsListActions>
) {
  return {
    sortModel: getSortModel(state),
    onSortModelChange: (model: GridSortModel) => {
      dispatch(setSortParamsAction(model));
    },
  };
}

export function applyLocale(date: Dayjs, language = "en") {
  const locale = dayJSLang[language];
  return date.locale(locale);
}

export function getClaimType(claimType: string) {
  if (claimType === JDP_SHORTAGE_OVERAGE)
    return t("claims:claims.type.jdpShortageAndOverage");
  if (claimType === VENDORE_DIRECT_SHORTAGE_OVERAGE)
    return t("claims:claims.type.vendorDirect");
  return claimType;
}
